<template>
  <article>
    <activity-stage
      :customRootContainerClass="`mathMaster ${keyboardEnabled ? '' : 'full-height'}`"
    >
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content" id="mathMasterContent">
        <div>
          <div id="squares">
            <square
              v-for="(step, index) in currentEquation?.steps"
              v-if="!isNaN(step.answer)"
              track-by="$index"
              :key="`number${index}`"
              :number="step.answer"
              :class="[{ answer: index === 4 }]"
            >
            </square>
          </div>
          <div style="position: relative">
            <label aria-live="polite" style="opacity: 0; position: absolute;">{{
              squaresDescription
            }}</label>
          </div>
          <div id="expression">
            <blinking-cursor-text
              v-for="(step, index) in currentEquation?.steps"
              :class="[
                'blink-text',
                { answer: index === 4 },
                { 'number-step': checkIfNumber(step.answer) }
              ]"
              :key="`step${index}`"
              :text="step.answer"
              :text-step="index"
            ></blinking-cursor-text>
          </div>
        </div>

        <div id="results" v-show="chooseAnswer" transition="fade">
          <div
            v-for="(answer, index) in currentRound.answers"
            :key="`result${index}`"
            track-by="$index"
            class="result focusable"
            :id="`res${index}`"
            tabindex="0"
            @click="isCorrectAnswer(answer)"
            @keyup.enter="isCorrectAnswer(answer)"
          >
            <square :number="answer.answer"></square>
            <p v-text="answer.answer"></p>
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';
import { isFinite, isNaN } from 'lodash';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';

import Square from 'components/activity/MathMasters/Square.vue';

export default {
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  components: {
    ActivityStage,
    Square,
    BlinkingCursorText
  },

  data() {
    return {
      activityName: 'Math Masters',
      currentEquation: {},
      chooseAnswer: false,
      showAnswer: false,
      squaresDescription: ''
    };
  },

  methods: {
    checkIfNumber(answer) {
      return isFinite(+answer);
    },

    async loadActivity() {
      this.createRounds();
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        keyboardType,
        hideNumberRow: false,
        roofs
      };
    },

    startActivity() {
      this.errorLoggerStart();
      this.setRound();

      this.$nextTick(this.setActivityContainerHeight);
    },

    setRound() {
      this.setupNewRound();
      this.enableKeyboard();

      this.$nextTick(() => {
        this.currentEquation = {
          steps: this.currentRound.steps.slice(0, 4)
        };
        this.showAnswer = false;
        this.chooseAnswer = false;
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
      });
    },

    handleCorrectKey(attempt) {
      this.incrementTypingIndex();
      this.sndType();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.progress.stepIndex === 3) {
        this.hideKeyboard();

        TweenMax.delayedCall(0.8, () => {
          this.squaresDescription = `Select the right answer to complete the math sentence: ${
            this.squaresDescription.split('Type the following math sentence: ')[1]
          }`;
          this.chooseAnswer = true;
          TweenMax.staggerFrom('.result', 0.8, { y: '130%' }, 0.5);
        });
      }

      if (this.isEndOfCurrentSentence) {
        this.setCurrentStep();
      }

      if (this.isEndOfRound) {
        this.sndBell();
        this.handleRoundEnd();
      }
    },

    handleRoundEnd() {
      const t = new TimelineLite();

      t.delay(0.8).call(() => {
        if (this.isLastRound) {
          TweenMax.delayedCall(0.8, this.completeActivity);
        } else {
          t.delay(1)
            .to('.activity-stage', 0.8, { opacity: 0 })
            .call(this.setRound);
        }
      });
    },

    isCorrectAnswer(answer) {
      if (answer.isCorrect) {
        this.setScreenReaderAlert('Correct');

        this.currentEquation = {
          steps: this.currentRound.steps
        };

        this.squaresDescription = `Type the following to complete the math sentence: ${answer.answer}`;
        this.showAnswer = true;
        this.chooseAnswer = false;

        this.enableKeyboard();
        this.showKeyboard();
        this.sndBell();

        return;
      }

      this.rerenderScreenReaderAlert('Incorrect, Try Again');
      this.sndError();
    },

    isNaN(number) {
      return isNaN(+number) ? true : false;
    }
  },

  watch: {
    currentEquation(newVal, oldVal) {
      if (newVal) {
        const steps = newVal.steps;

        const operationMap = {
          '+': 'plus',
          '-': 'minus',
          '*': 'times',
          '/': 'divided by'
        };

        const instructions = `Type the following math sentence: ${steps[0].answer} ${steps[1].answer} ${steps[2].answer} =`;

        if (steps.length === 4) this.squaresDescription = instructions;
      }
    },
    result() {
      TweenMax.to('.answer', 0, { autoAlpha: 0 });
      TweenMax.to('.answer', 0.8, { autoAlpha: 1 });
    }
  }
};
</script>

<style lang="scss">
.mathMaster {
  #mathMasterContent {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 100%;
    min-width: 100%;
    height: max-content;
    width: max-content;
    overflow: hidden;
  }
  #squares,
  #expression {
    display: flex;
    flex-flow: row nowrap;
    justify-content: start;
    padding: 20px 0 0 20px;
    min-width: calc(100% - 20px);
    width: max-content;

    .square {
      margin: 0 20px;
    }

    .answer {
      margin: 0 20px 0 40px;
    }
  }

  #expression {
    padding: 15px 0 0 20px;
  }

  #results {
    padding: 0px;
    min-width: 100%;
    width: max-content;
    display: flex;
    justify-content: space-around;

    .square {
      margin: 0 5px;
    }

    .result {
      display: inline-block;

      p {
        text-align: center;
        margin: 6.5% 0;
        font-size: 2.5vw;
        margin: 6.5% 0;
      }
    }
  }

  #expression {
    .blink-text {
      text-align: center;
      padding: 0;
      font-size: 20px;
    }

    .number-step {
      width: 216px;
      margin: 0 20px;
    }
  }

  .tablet {
    .square {
      transform: scale(0.8);
    }
  }
}
</style>
